import { useMutation, useQueryClient } from "@tanstack/react-query";
import NetInfo from '@react-native-community/netinfo';
import { savePerson, bulkUpdatePeople, updateAvatarUpdatedAt } from "@/data/people";
import { QueryKeys } from "@/utils/react-query/QueryKeys";
import { createShortcut } from "@/data/shortcuts";
import { saveEmailTemplate, deleteEmailTemplate } from "@/data/emails";
import { createNote, updateNote, deleteNote } from "@/data/notes";
import { logFollowUpTouch, correctFollowUpTouch, retractFollowUpTouch, setPrimaryCaregiver, setMonitoringMode, resolveContactWarning, createFollowUpTask, completeFollowUpTask, assignFollowUpTask, cancelFollowUpTask, upsertFollowUpAudience, deleteFollowUpAudience, snoozeFollowUpAttention, publishFollowUpPath, startFollowUpPath, stopFollowUpEnrollment, resolveFollowUpStep, startFollowUpCare, setFollowUpMealDates, claimFollowUpMeal, closeFollowUpCare, updateFollowUpCareDetails } from "@/data/followUp";
import { createProfileView, updateProfileView, deleteProfileView } from "@/data/profileViews";
import { recordAutomationApproval } from "@/data/automations";

/**
 * Checks if an error is a Supabase network error that should be retried.
 * @param {Error} error - The error to check.
 * @returns {boolean} - True if the error is a retryable network error.
 */
export const isSupabaseNetworkError = (error) => {
    if (!error) return false;
    
    const errorMessage = error?.message?.toLowerCase() || '';
    const errorCode = error?.code?.toLowerCase() || '';
    
    const networkErrorPatterns = [
        'network',
        'fetch',
        'timed out',
        'timeout',
        'econnrefused',
        'econnreset',
        'enotfound',
        'socket',
        'dns',
        'ssl',
        'certificate',
        'abort',
        'failed to fetch',
        'load failed',
        'networkerror',
        'connection',
        // Queue-full rejects before the request is ever sent, so retrying a
        // mutation here is safe (CHURCH-APP-1F6/14J).
        'request queue is full',
    ];
    
    return networkErrorPatterns.some(pattern => 
        errorMessage.includes(pattern) || errorCode.includes(pattern)
    );
};

/**
 * Custom retry function for react-query mutations that handles Supabase network errors.
 * Retries up to 4 times with exponential backoff, but only if device is online.
 * @param {number} failureCount - Number of times the mutation has failed.
 * @param {Error} error - The error that caused the failure.
 * @returns {boolean} - True if the mutation should be retried.
 */
export const shouldRetryMutation = async (failureCount, error) => {
    if (failureCount >= 4) return false;
    
    const netInfo = await NetInfo.fetch();
    if (!netInfo.isConnected) return false;
    
    return isSupabaseNetworkError(error);
};

/**
 * Calculates the retry delay with exponential backoff.
 * @param {number} attemptIndex - The current attempt index (0-based).
 * @returns {number} - Delay in milliseconds.
 */
export const getRetryDelay = (attemptIndex) => {
    return Math.min(1000 * 2 ** attemptIndex, 30000);
};

/**
 * Default mutation options with retry logic for network errors.
 */
export const defaultMutationOptions = {
    retry: shouldRetryMutation,
    retryDelay: getRetryDelay,
    onError: (error) => {
        __DEV__ && console.error('Mutation failed:', error);
    },
};

/**
 * Mutation keys for cache invalidation and identification.
 */
export const MutationKeys = {
    people: {
        save: () => ['savePerson'],
        bulkUpdate: () => ['bulkUpdatePeople'],
        updateAvatar: () => ['updateAvatarUpdatedAt'],
    },
    shortcuts: {
        save: () => ['saveShortcut'],
    },
    emailTemplates: {
        save: () => ['saveEmailTemplate'],
        delete: () => ['deleteEmailTemplate'],
    },
    peopleNotes: {
        save: () => ['savePersonNote'],
        delete: () => ['deletePersonNote'],
    },
    followUp: {
        logTouch: () => ['logFollowUpTouch'],
        correctTouch: () => ['correctFollowUpTouch'],
        retractTouch: () => ['retractFollowUpTouch'],
        setPrimaryCaregiver: () => ['setFollowUpPrimaryCaregiver'],
        setMonitoringMode: () => ['setFollowUpMonitoringMode'],
        resolveContactWarning: () => ['resolveFollowUpContactWarning'],
        createTask: () => ['createFollowUpTask'],
        completeTask: () => ['completeFollowUpTask'],
        assignTask: () => ['assignFollowUpTask'],
        cancelTask: () => ['cancelFollowUpTask'],
        upsertAudience: () => ['upsertFollowUpAudience'],
        deleteAudience: () => ['deleteFollowUpAudience'],
        snoozeAttention: () => ['snoozeFollowUpAttention'],
        publishPath: () => ['publishFollowUpPath'],
        startPath: () => ['startFollowUpPath'],
        stopEnrollment: () => ['stopFollowUpEnrollment'],
        resolveStep: () => ['resolveFollowUpStep'],
        startCare: () => ['startFollowUpCare'],
        setMealDates: () => ['setFollowUpMealDates'],
        claimMeal: () => ['claimFollowUpMeal'],
        closeCare: () => ['closeFollowUpCare'],
        updateCareDetails: () => ['updateFollowUpCareDetails'],
    },
    profileViews: {
        save: () => ['saveProfileView'],
        delete: () => ['deleteProfileView'],
    },
    automations: {
        recordApproval: () => ['recordAutomationApproval'],
    },
};

/**
 * Follow Up derived-data invalidation (P5). Touches, caregiver/monitoring
 * changes and task lifecycle all feed the Overview (needsAttention,
 * needsOwner, dueWork, recentTouches), the monitored People list, and the
 * caller's My Follow Up — prefix-invalidate every filter variant at once.
 * Called with the org id carried in every followUp mutation's variables.
 */
const invalidateFollowUpDerived = (queryClient, organizationID) => {
    if (!organizationID) return;
    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'overview'] });
    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'people'] });
    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'my'] });
    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'needsOwner'] });
};

/** Derived data + org configuration surfaces (settings save / audience edit / snooze). */
const invalidateFollowUpConfig = (queryClient, organizationID) => {
    invalidateFollowUpDerived(queryClient, organizationID);
    if (!organizationID) return;
    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'settings'] });
    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'audiences'] });
};

/**
 * P6 — Paths/enrollment surfaces. Lifecycle changes ripple into the
 * enrollment detail, every enrollment list (per-path participants,
 * per-person journeys), the template detail (progress/revision), the
 * person's care surface (open-care-work inputs), and derived monitoring.
 */
/**
 * §25.9 — care episode lifecycle changes ripple into the episode detail,
 * every scoped episode list (dashboard Active care, person surface), and
 * the shared derived surfaces (overview activeCareEpisodes cards).
 */
const invalidateFollowUpCare = (queryClient, organizationID, { careEpisodeID, personID } = {}) => {
    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'careEpisodes'] });
    if (careEpisodeID) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.careEpisode(organizationID, careEpisodeID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.careClosePreview(organizationID, careEpisodeID) });
    }
    if (personID) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personTasks(organizationID, personID) });
    }
    invalidateFollowUpDerived(queryClient, organizationID);
    // The main Dashboard renders its own bounded care list.
    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
};

const invalidateFollowUpPaths = (queryClient, organizationID, { enrollmentID, personID, pathID } = {}) => {
    if (!organizationID) return;
    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'paths'] });
    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'enrollments'] });
    if (enrollmentID) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.enrollment(organizationID, enrollmentID) });
    }
    if (pathID) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.path(organizationID, pathID) });
    }
    if (personID) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personTasks(organizationID, personID) });
    }
    invalidateFollowUpDerived(queryClient, organizationID);
};

/**
 * Mutation hooks organized by domain.
 * Each mutation includes network error retry logic with exponential backoff.
 */
export const Mutation = {
    people: {
        /**
         * Saves a person (create or update).
         * @param {object} options - Additional mutation options including onSuccess, onError callbacks.
         * @returns {UseMutationResult} - The mutation result object.
         */
        save: (options = {}) => {
            const queryClient = useQueryClient();
            
            return useMutation({
                mutationKey: MutationKeys.people.save(),
                mutationFn: savePerson,
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(data.id) });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * Bulk updates scalar and custom field values for multiple people.
         * @param {object} options - Additional mutation options including onSuccess, onError callbacks.
         * @returns {UseMutationResult} - The mutation result object.
         */
        bulkUpdate: (options = {}) => {
            const queryClient = useQueryClient();

            return useMutation({
                mutationKey: MutationKeys.people.bulkUpdate(),
                mutationFn: bulkUpdatePeople,
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * Updates the avatar timestamp for a person.
         * @param {object} options - Additional mutation options including onSuccess, onError callbacks.
         * @returns {UseMutationResult} - The mutation result object.
         */
        updateAvatar: (options = {}) => {
            return useMutation({
                mutationKey: MutationKeys.people.updateAvatar(),
                mutationFn: updateAvatarUpdatedAt,
                ...defaultMutationOptions,
                ...options,
            });
        },
    },
    
    shortcuts: {
        save: (options = {}) => {
            return useMutation({
                mutationKey: MutationKeys.shortcuts.save(),
                mutationFn: createShortcut,
                ...defaultMutationOptions,
                ...options,
            });
        },
    },
    emailTemplates: {
        save: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.emailTemplates.save(),
                mutationFn: saveEmailTemplate,
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.emailTemplates.all() });
                    if (data?.id) queryClient.invalidateQueries({ queryKey: QueryKeys.emailTemplates.byId(data.id) });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },
        delete: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.emailTemplates.delete(),
                mutationFn: (id) => deleteEmailTemplate(id),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.emailTemplates.all() });
                    if (variables) queryClient.invalidateQueries({ queryKey: QueryKeys.emailTemplates.byId(variables) });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },
    },
    peopleNotes: {
        save: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.peopleNotes.save(),
                mutationFn: ({ id, ...rest }) => id ? updateNote({ id, ...rest }) : createNote(rest),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    queryClient.invalidateQueries({
                        queryKey: QueryKeys.peopleNotes.byPersonId(variables.personID),
                    });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        delete: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.peopleNotes.delete(),
                mutationFn: ({ id }) => deleteNote(id),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    queryClient.invalidateQueries({
                        queryKey: QueryKeys.peopleNotes.byPersonId(variables.personID),
                    });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },
    },
    followUp: {
        // Shared invalidation for every followUp mutation: the person care
        // surface, the caregiver's My Follow Up list, the linked-notes list,
        // and the standard person-notes list (a touch can create a note).
        // variables must carry { organizationID, personID } for scoping.
        logTouch: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.logTouch(),
                mutationFn: ({ payload }) => logFollowUpTouch(payload),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, personID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personNotes(organizationID, personID) });
                    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'myPeople'] });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    if (data?.noteID || variables?.payload?.note?.content) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.peopleNotes.byPersonId(personID) });
                    }
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        correctTouch: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.correctTouch(),
                mutationFn: ({ touchID, patch }) => correctFollowUpTouch({ touchID, patch }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, personID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        retractTouch: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.retractTouch(),
                mutationFn: ({ touchID, reason }) => retractFollowUpTouch({ touchID, reason }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, personID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        setPrimaryCaregiver: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.setPrimaryCaregiver(),
                mutationFn: ({ personID, caregiverPersonID, reason }) => setPrimaryCaregiver({ personID, caregiverPersonID, reason }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, personID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'myPeople'] });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        setMonitoringMode: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.setMonitoringMode(),
                mutationFn: ({ personID, mode, reason }) => setMonitoringMode({ personID, mode, reason }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, personID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        resolveContactWarning: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.resolveContactWarning(),
                mutationFn: ({ personID, resolution, notes }) => resolveContactWarning({ personID, resolution, notes }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, personID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'myPeople'] });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        createTask: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.createTask(),
                mutationFn: ({ payload }) => createFollowUpTask(payload),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, personID, enrollmentID, careEpisodeID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personTasks(organizationID, personID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.needsOwner(organizationID) });
                    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'myPeople'] });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.all() });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    // Episode-linked tasks surface under the episode's plannedTasks.
                    if (careEpisodeID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.careEpisode(organizationID, careEpisodeID) });
                    }
                    if (enrollmentID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.enrollment(organizationID, enrollmentID) });
                    }
                    if (data?.taskInstanceID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.assignedTask(organizationID, data.taskInstanceID) });
                    }
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * Complete a care task through follow_up_complete_task — the only
         * permitted completion path (the server rejects direct
         * TaskInstances.completed writes with
         * FOLLOW_UP_COMPLETION_VIA_RPC_REQUIRED). variables:
         * { organizationID, taskInstanceID, payload, personID?, loggedInPersonID? }
         * FOLLOW_UP_CONFLICT = already completed/cancelled — callers should
         * refetch and present the current state, not an error.
         */
        completeTask: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.completeTask(),
                mutationFn: ({ payload }) => completeFollowUpTask(payload),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, taskInstanceID, personID, loggedInPersonID, enrollmentID, careEpisodeID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.assignedTask(organizationID, taskInstanceID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(taskInstanceID) });
                    // A step-task completion resolves its linked progress —
                    // enrollment views always refresh (§25.7).
                    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'enrollments'] });
                    if (enrollmentID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.enrollment(organizationID, enrollmentID) });
                    }
                    if (careEpisodeID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.careEpisode(organizationID, careEpisodeID) });
                    }
                    queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks(loggedInPersonID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments(loggedInPersonID) });
                    queryClient.invalidateQueries({ queryKey: ['upcomingTeamTaskInstances'] });
                    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.needsOwner(organizationID) });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    if (personID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personTasks(organizationID, personID) });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.personHistory(personID) });
                        // A completion touch can create a note.
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personNotes(organizationID, personID) });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.peopleNotes.byPersonId(personID) });
                    }
                    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'myPeople'] });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        assignTask: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.assignTask(),
                mutationFn: ({ taskInstanceID, assigneePersonID }) => assignFollowUpTask({ taskInstanceID, assigneePersonID }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, taskInstanceID, personID, careEpisodeID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.assignedTask(organizationID, taskInstanceID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(taskInstanceID) });
                    queryClient.invalidateQueries({ queryKey: ['upcomingTasks'] });
                    queryClient.invalidateQueries({ queryKey: ['upcomingTaskAssignments'] });
                    queryClient.invalidateQueries({ queryKey: ['upcomingTeamTaskInstances'] });
                    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.needsOwner(organizationID) });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    if (personID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personTasks(organizationID, personID) });
                    }
                    if (careEpisodeID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.careEpisode(organizationID, careEpisodeID) });
                    }
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        cancelTask: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.cancelTask(),
                mutationFn: ({ taskInstanceID, reason }) => cancelFollowUpTask({ taskInstanceID, reason }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, taskInstanceID, personID, loggedInPersonID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.assignedTask(organizationID, taskInstanceID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(taskInstanceID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks(loggedInPersonID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments(loggedInPersonID) });
                    queryClient.invalidateQueries({ queryKey: ['upcomingTeamTaskInstances'] });
                    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.needsOwner(organizationID) });
                    invalidateFollowUpDerived(queryClient, organizationID);
                    if (personID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personTasks(organizationID, personID) });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.personHistory(personID) });
                    }
                    queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'myPeople'] });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * P5 — audience create/update (manage-only). Returns
         * row + membershipPreview; callers surface the preview.
         * variables: { organizationID, payload }.
         */
        upsertAudience: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.upsertAudience(),
                mutationFn: ({ payload }) => upsertFollowUpAudience(payload),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    invalidateFollowUpConfig(queryClient, variables?.organizationID);
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * Audience delete (manage-only). FK links SET NULL server-side —
         * explicit person includes and history are never removed.
         * variables: { organizationID, audienceID }.
         */
        deleteAudience: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.deleteAudience(),
                mutationFn: ({ audienceID }) => deleteFollowUpAudience(audienceID),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    invalidateFollowUpConfig(queryClient, variables?.organizationID);
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * P5 — reason-scoped attention snooze (manage-only). Snoozing one
         * reason must never hide the person's other reasons (spec §11.6).
         * variables: { organizationID, personID, reason, audienceID?, until,
         * snoozeReason? }.
         */
        snoozeAttention: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.snoozeAttention(),
                mutationFn: ({ personID, reason, audienceID, until, snoozeReason }) =>
                    snoozeFollowUpAttention({ personID, reason, audienceID, until, snoozeReason }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, personID } = variables;
                    invalidateFollowUpConfig(queryClient, organizationID);
                    if (personID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
                    }
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * P6 — atomic template publish (manage-only). variables:
         * { organizationID, payload } — payload carries expectedRevision on
         * update; FOLLOW_UP_CONFLICT means the definition moved under the
         * editor and callers should refetch + re-offer.
         */
        publishPath: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.publishPath(),
                mutationFn: ({ payload }) => publishFollowUpPath(payload),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    invalidateFollowUpPaths(queryClient, variables?.organizationID, {
                        pathID: data?.pathID ?? variables?.payload?.pathID,
                    });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * Flat Path enrollment start (§25 — team). variables:
         * { organizationID, personID, pathID, idempotencyKey? }.
         * data.alreadyOpen means an open enrollment already exists — the
         * caller navigates to it instead of showing an error.
         */
        startPath: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.startPath(),
                mutationFn: ({ personID, pathID, idempotencyKey }) =>
                    startFollowUpPath({ personID, pathID, idempotencyKey }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    invalidateFollowUpPaths(queryClient, variables?.organizationID, {
                        enrollmentID: data?.enrollmentID,
                        personID: variables?.personID,
                        pathID: variables?.pathID,
                    });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * Stop Path (§25.6 — manage OR the person's primary caregiver).
         * Atomically cancels this enrollment's unfinished work; a restart
         * is a new enrollment. variables: { organizationID, enrollmentID,
         * personID, pathID?, reason?, stopTouchID? }.
         */
        stopEnrollment: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.stopEnrollment(),
                mutationFn: ({ enrollmentID, reason, stopTouchID }) =>
                    stopFollowUpEnrollment({ enrollmentID, reason, stopTouchID }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, enrollmentID, personID, pathID } = variables;
                    invalidateFollowUpPaths(queryClient, organizationID, { enrollmentID, personID, pathID });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * Manager resolution of a blocked/current step (§25.6 — SKIP or
         * RETRY). variables: { organizationID, enrollmentID, personID,
         * stepProgressID, action, reason? }.
         */
        resolveStep: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.resolveStep(),
                mutationFn: ({ stepProgressID, action, reason }) =>
                    resolveFollowUpStep({ stepProgressID, action, reason }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, enrollmentID, personID, pathID } = variables;
                    invalidateFollowUpPaths(queryClient, organizationID, { enrollmentID, personID, pathID });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * §25.9 — template-free care intake (team or primary caregiver).
         * variables: { organizationID, personID, payload } — payload:
         * {personID, context?, careDetails?, notifyPersonIDs?,
         * idempotencyKey?}. alreadyOpen means an active episode exists —
         * the caller navigates to it, never an error.
         */
        startCare: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.startCare(),
                mutationFn: ({ payload }) => startFollowUpCare(payload),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, personID } = variables;
                    invalidateFollowUpCare(queryClient, organizationID, {
                        careEpisodeID: data?.careEpisodeID,
                        personID: personID ?? data?.personID,
                    });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * §25.9 — enable/edit/disable meal dates on a care episode (manage
         * or primary caregiver). variables: { organizationID, careEpisodeID,
         * personID, payload } — payload is the exact set_meal_dates shape
         * (careEpisodeID/enabled/dates/deliveryInstructions/deliveryWindow/
         * confirmImpact). A response with applied:false is a preview, not
         * an error — the caller renders impact and resubmits with
         * confirmImpact.
         */
        setMealDates: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.setMealDates(),
                mutationFn: ({ payload }) => setFollowUpMealDates(payload),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, careEpisodeID, personID } = variables;
                    // A pure preview changed nothing — skip invalidation.
                    if (data?.applied === false) {
                        options.onSuccess?.(data, variables, context);
                        return;
                    }
                    invalidateFollowUpCare(queryClient, organizationID, { careEpisodeID, personID });
                    queryClient.invalidateQueries({ queryKey: ['upcomingTasks'] });
                    queryClient.invalidateQueries({ queryKey: ['upcomingTaskAssignments'] });
                    queryClient.invalidateQueries({ queryKey: ['upcomingTeamTaskInstances'] });
                    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * Team-member meal-slot claim (capacity one). variables:
         * { organizationID, careEpisodeID, personID, mealSlotID,
         * idempotencyKey?, loggedInPersonID? }. FOLLOW_UP_CONFLICT means the
         * claim race was lost — never optimistically show the claim; the
         * invalidation brings the staffed row down.
         */
        claimMeal: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.claimMeal(),
                mutationFn: ({ mealSlotID, idempotencyKey }) =>
                    claimFollowUpMeal({ mealSlotID, idempotencyKey }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, careEpisodeID, personID, loggedInPersonID } = variables;
                    invalidateFollowUpCare(queryClient, organizationID, { careEpisodeID, personID });
                    if (data?.taskInstanceID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.assignedTask(organizationID, data.taskInstanceID) });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(data.taskInstanceID) });
                    }
                    if (loggedInPersonID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks(loggedInPersonID) });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments(loggedInPersonID) });
                    } else {
                        queryClient.invalidateQueries({ queryKey: ['upcomingTasks'] });
                        queryClient.invalidateQueries({ queryKey: ['upcomingTaskAssignments'] });
                    }
                    queryClient.invalidateQueries({ queryKey: ['upcomingTeamTaskInstances'] });
                    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * §25.9 — explicit care closure (manage or primary caregiver).
         * variables: { organizationID, careEpisodeID, personID, payload } —
         * payload carries careEpisodeID/cancelTaskInstanceIDs/
         * keepTaskInstanceIDs/closureNote/idempotencyKey. Result echoes
         * pendingWork + skippedTaskDecisions (C13) — the caller renders the
         * summary, nothing is auto-completed or deleted.
         */
        closeCare: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.closeCare(),
                mutationFn: ({ payload }) => closeFollowUpCare(payload),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, careEpisodeID, personID } = variables;
                    invalidateFollowUpCare(queryClient, organizationID, { careEpisodeID, personID });
                    if (personID) {
                        // An optional closure note is written as a PeopleNote.
                        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personNotes(organizationID, personID) });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.peopleNotes.byPersonId(personID) });
                    }
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        /**
         * §25.9 — RFC 7396 merge-patch on a care episode's careDetails
         * (manage or primary caregiver, same gate as closeCare). variables:
         * { organizationID, careEpisodeID, personID, patch } — patch carries
         * ONLY changed keys; a key set to null deletes it server-side. Only
         * ACTIVE episodes accept edits.
         */
        updateCareDetails: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.followUp.updateCareDetails(),
                mutationFn: ({ careEpisodeID, patch }) =>
                    updateFollowUpCareDetails({ careEpisodeID, patch }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { organizationID, careEpisodeID, personID } = variables;
                    invalidateFollowUpCare(queryClient, organizationID, { careEpisodeID, personID });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },
    },
    automations: {
        /**
         * Record an approver decision on a REQUEST_APPROVAL task instance via
         * automation_record_approval (§P10 contract — first response wins).
         * variables: { taskInstanceID, decision: 'APPROVED'|'DENIED', note?,
         *             loggedInPersonID? }
         * Returns {decision} or {already:true, outcome} — callers should
         * surface the already-decided state rather than an error.
         */
        recordApproval: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.automations.recordApproval(),
                mutationFn: ({ taskInstanceID, decision, note }) =>
                    recordAutomationApproval({ taskInstanceID, decision, note }),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    const { taskInstanceID, loggedInPersonID } = variables;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(taskInstanceID) });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.paths.enrollmentContext(taskInstanceID) });
                    if (loggedInPersonID) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks(loggedInPersonID) });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments(loggedInPersonID) });
                    }
                    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },
    },
    profileViews: {
        save: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.profileViews.save(),
                mutationFn: ({ id, ...rest }) => id ? updateProfileView({ id, ...rest }) : createProfileView(rest),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.profileViews.all() });
                    if (data?.id) {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.profileViews.byId(data.id) });
                        queryClient.invalidateQueries({ queryKey: ['profileViews', 'card'] });
                    }
                    options.onSuccess?.(data, variables, context);
                },
            });
        },

        delete: (options = {}) => {
            const queryClient = useQueryClient();
            return useMutation({
                mutationKey: MutationKeys.profileViews.delete(),
                mutationFn: ({ id }) => deleteProfileView(id),
                ...defaultMutationOptions,
                ...options,
                onSuccess: (data, variables, context) => {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.profileViews.all() });
                    if (variables?.id) queryClient.invalidateQueries({ queryKey: QueryKeys.profileViews.byId(variables.id) });
                    options.onSuccess?.(data, variables, context);
                },
            });
        },
    },
};
